import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import PasswordInput from '../components/PasswordInput';
import './InvitationCompletion.css';

const InvitationCompletion = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // Form states
  const [step, setStep] = useState('validating'); // 'validating', 'form', 'creating', 'success', 'error'
  const [invitation, setInvitation] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  
  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  
  useEffect(() => {
    validateInvitationToken();
  }, [token]);
  
  const validateInvitationToken = async () => {
    try {
      setError('');
      
      // Validate the invitation token with the backend
      const response = await apiClient.get(`/admin/invitations/validate/${token}`);
      
      if (response.data.success) {
        setInvitation(response.data.invitation);
        setStep('form');
        
        // Pre-populate email from invitation
        setFormData(prev => ({
          ...prev,
          email: response.data.invitation.email
        }));
      } else {
        setError(response.data.message || 'Invalid invitation link');
        setStep('error');
      }
    } catch (err) {
      console.error('Invitation validation error:', err);
      
      if (err.response?.status === 404) {
        setError('Invitation not found or has expired');
      } else if (err.response?.status === 410) {
        setError('This invitation has already been used');
      } else {
        setError(err.response?.data?.message || 'Failed to validate invitation');
      }
      setStep('error');
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    // First name validation
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    // Last name validation
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    setStep('creating');
    
    try {
      // Complete the invitation by creating the user account via Stytch B2B
      const response = await apiClient.post(`/admin/invitations/complete`, {
        invitation_token: token,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        password: formData.password
      });
      
      if (response.data.success) {
        // Store the access token if provided
        if (response.data.access_token) {
          localStorage.setItem('auth_token', response.data.access_token);
        }
        
        setStep('success');
        
        // Redirect to dashboard after success message
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to complete invitation');
        setStep('form');
      }
    } catch (err) {
      console.error('Invitation completion error:', err);
      
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.detail || 
                          'Failed to create your account';
      
      setError(errorMessage);
      setStep('form');
    } finally {
      setIsLoading(false);
    }
  };
  
  const renderValidatingState = () => (
    <div className="invitation-state">
      <div className="loading-icon">
        <i className="fas fa-spinner fa-spin"></i>
      </div>
      <h3>Validating Invitation</h3>
      <p>Please wait while we verify your invitation link...</p>
    </div>
  );
  
  const renderForm = () => (
    <div className="invitation-form">
      <div className="invitation-header">
        <h2>Complete Your Account</h2>
        <p>You've been invited to join <strong>{invitation?.organization_name}</strong></p>
        <div className="invitation-details">
          <p><strong>Email:</strong> {invitation?.email}</p>
          <p><strong>Role:</strong> {invitation?.role}</p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>
              <i className="fas fa-user"></i>
              First Name *
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              required
              placeholder="Enter your first name"
              disabled={isLoading}
              className={validationErrors.firstName ? 'error' : ''}
            />
            {validationErrors.firstName && (
              <div className="error-message">{validationErrors.firstName}</div>
            )}
          </div>
          
          <div className="form-group">
            <label>
              <i className="fas fa-user"></i>
              Last Name *
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              required
              placeholder="Enter your last name"
              disabled={isLoading}
              className={validationErrors.lastName ? 'error' : ''}
            />
            {validationErrors.lastName && (
              <div className="error-message">{validationErrors.lastName}</div>
            )}
          </div>
        </div>
        
        <div className="form-group">
          <label>
            <i className="fas fa-lock"></i>
            Create Password *
          </label>
          <PasswordInput
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            required
            placeholder="Create a secure password"
            disabled={isLoading}
            className={validationErrors.password ? 'error' : ''}
          />
          {validationErrors.password && (
            <div className="error-message">{validationErrors.password}</div>
          )}
          <div className="password-requirements">
            <p>Password must:</p>
            <ul>
              <li className={formData.password.length >= 8 ? 'valid' : ''}>
                Be at least 8 characters long
              </li>
              <li className={/(?=.*[a-z])/.test(formData.password) ? 'valid' : ''}>
                Contain at least one lowercase letter
              </li>
              <li className={/(?=.*[A-Z])/.test(formData.password) ? 'valid' : ''}>
                Contain at least one uppercase letter
              </li>
              <li className={/(?=.*\d)/.test(formData.password) ? 'valid' : ''}>
                Contain at least one number
              </li>
            </ul>
          </div>
        </div>
        
        <div className="form-group">
          <label>
            <i className="fas fa-lock"></i>
            Confirm Password *
          </label>
          <PasswordInput
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            required
            placeholder="Confirm your password"
            disabled={isLoading}
            className={validationErrors.confirmPassword ? 'error' : ''}
          />
          {validationErrors.confirmPassword && (
            <div className="error-message">{validationErrors.confirmPassword}</div>
          )}
        </div>
        
        <button 
          type="submit"
          className="submit-btn"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Creating Account...
            </>
          ) : (
            <>
              <i className="fas fa-user-plus"></i>
              Complete Registration
            </>
          )}
        </button>
      </form>
    </div>
  );
  
  const renderCreatingState = () => (
    <div className="invitation-state">
      <div className="loading-icon">
        <i className="fas fa-spinner fa-spin"></i>
      </div>
      <h3>Creating Your Account</h3>
      <p>Please wait while we set up your account...</p>
    </div>
  );
  
  const renderSuccessState = () => (
    <div className="invitation-state success">
      <div className="success-icon">
        <i className="fas fa-check-circle"></i>
      </div>
      <h3>Welcome to {invitation?.organization_name}!</h3>
      <p>Your account has been created successfully.</p>
      <p>Redirecting you to the dashboard...</p>
    </div>
  );
  
  const renderErrorState = () => (
    <div className="invitation-state error">
      <div className="error-icon">
        <i className="fas fa-exclamation-triangle"></i>
      </div>
      <h3>Invitation Error</h3>
      <p>{error}</p>
      <button 
        className="retry-btn"
        onClick={() => window.location.href = '/login'}
      >
        <i className="fas fa-sign-in-alt"></i>
        Go to Login
      </button>
    </div>
  );
  
  return (
    <div className="invitation-completion">
      <div className="invitation-container">
        <div className="invitation-wrapper">
          <div className="invitation-branding">
            <h1>MedInsight PRO</h1>
            <p>Complete your account setup</p>
          </div>
          
          {error && step === 'form' && (
            <div className="alert alert-error">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}
          
          {step === 'validating' && renderValidatingState()}
          {step === 'form' && renderForm()}
          {step === 'creating' && renderCreatingState()}
          {step === 'success' && renderSuccessState()}
          {step === 'error' && renderErrorState()}
        </div>
      </div>
    </div>
  );
};

export default InvitationCompletion;